import React, { useState, useEffect, useCallback } from 'react';
import {
  Container, Box, Grid, Paper, Typography, TextField, Button, FormControlLabel,
  Checkbox, Alert, CircularProgress, Divider, useMediaQuery, useTheme
} from '@mui/material';
import { Store, Save, People } from '@mui/icons-material';
import { MenuInicial } from './MenuInicial';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  API_BASE_URL, SUCURSAL_FORMDATA, SUCURSAL_GET, SUCURSAL_ALTA, SUCURSAL_ACTUALIZAR, FACTURACION_GET
} from '../services/apiConfig';
import TituloPagina from './utils/TituloPagina.jsx';
import VolverBreadcrumb from './utils/VolverBreadcrumb.jsx';
import Cargando from './utils/Cargando.jsx';
import { avisarExito, avisarError } from '../services/toast';
import axios from 'axios';

// Chequeo simple, no pretende cubrir el 100% del RFC -- mismo criterio que
// ya usa el backend (validarMailsSucursal() en config/funciones.php), solo
// para avisar antes de mandar el form en vez de esperar el error del server.
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const authHeader = () => {
  const t = sessionStorage.getItem('authToken');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

function Sucursal() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [params] = useSearchParams();
  const id = params.get('id');
  const modo = id ? 'editar' : 'crear';

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const [usuarios, setUsuarios] = useState([]);          // todos los usuarios
  const [asignados, setAsignados] = useState([]);        // ids asignados
  const [form, setForm] = useState({ nombre: '', direccion: '', wap: '', mail: '', precio_envio: '', punto_venta: '' });
  const set = (campo, valor) => setForm((f) => ({ ...f, [campo]: valor }));
  const [facturacionHabilitada, setFacturacionHabilitada] = useState(false);

  useEffect(() => {
    axios.get(API_BASE_URL + FACTURACION_GET, { headers: authHeader() })
      .then((res) => setFacturacionHabilitada(!!Number(res.data?.data?.habilitado)))
      .catch(() => {});
  }, []);

  const toggleUsuario = (uid) => setAsignados((prev) =>
    prev.includes(uid) ? prev.filter((x) => x !== uid) : [...prev, uid]);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const fd = await axios.get(API_BASE_URL + SUCURSAL_FORMDATA, { headers: authHeader() });
      setUsuarios(fd.data?.data?.usuarios ?? []);
      if (id) {
        const res = await axios.get(API_BASE_URL + SUCURSAL_GET, { params: { id }, headers: authHeader() });
        const s = res.data?.data;
        if (s) {
          setForm({
            nombre: s.nombre ?? '', direccion: s.direccion ?? '', wap: s.wap ?? '',
            mail: s.mail ?? '', precio_envio: s.precio_envio ?? '', punto_venta: s.punto_venta ?? '',
          });
          setAsignados((s.usuarios ?? []).map(Number));
        }
      }
    } catch (e) {
      setError(e.response?.data?.message || 'Error al cargar datos.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { cargar(); }, [cargar]);

  const guardar = async () => {
    setError('');
    if (!String(form.nombre).trim()) { setError('El nombre es requerido.'); return; }

    const mailRaw = String(form.mail || '').trim();
    if (mailRaw !== '') {
      const partes = mailRaw.split(',').map((p) => p.trim()).filter((p) => p !== '');
      if (partes.length === 0) { setError('El mail no es válido.'); return; }
      const invalido = partes.find((p) => !EMAIL_REGEX.test(p));
      if (invalido) { setError(`El mail "${invalido}" no es válido.`); return; }
    }

    setSubmitting(true);
    const payload = { ...form, usuarios: asignados };
    try {
      if (modo === 'editar') {
        const res = await axios.post(API_BASE_URL + SUCURSAL_ACTUALIZAR, { id: Number(id), ...payload }, { headers: authHeader() });
        if (!res.data?.success) throw new Error(res.data?.message || 'Error al guardar.');
        avisarExito('Sucursal actualizada.');
      } else {
        const res = await axios.post(API_BASE_URL + SUCURSAL_ALTA, payload, { headers: authHeader() });
        if (!res.data?.success) throw new Error(res.data?.message || 'Error al guardar.');
        avisarExito('Sucursal creada.');
      }
      setTimeout(() => navigate('/sucursales'), 900);
    } catch (e) {
      avisarError(e.response?.data?.message || e.message || 'Error al guardar.');
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth={isMobile ? false : 'xl'} sx={{ px: isMobile ? 1 : 3 }}>
        <MenuInicial /><Cargando />
      </Container>
    );
  }

  return (
    <Container maxWidth={isMobile ? false : 'xl'} sx={{ px: isMobile ? 1 : 3, pb: 4 }}>
      <MenuInicial />
      <Box sx={{ my: 3 }}>
        <TituloPagina
          icon={Store}
          titulo={modo === 'editar' ? `Editar sucursal #${id}` : 'Nueva sucursal'}
          subtitulo="Datos de la sucursal y usuarios asignados"
          ayuda="El/los mail(es) configurados acá reciben el resumen automático cada vez que se cierra un turno de esta sucursal. Podés poner varios separados por coma — el primero es el destinatario principal y el resto queda en copia (Cc)."
        />
        <VolverBreadcrumb to="/sucursales" label="Sucursales" />

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField fullWidth size="small" label="Nombre *" value={form.nombre} onChange={(e) => set('nombre', e.target.value)} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth size="small" label="Dirección" value={form.direccion} onChange={(e) => set('direccion', e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField fullWidth size="small" label="WhatsApp" value={form.wap} onChange={(e) => set('wap', e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={6} md={5}>
              <TextField
                fullWidth size="small" label="Mail" value={form.mail} onChange={(e) => set('mail', e.target.value)}
                helperText="Uno o varios, separados por coma"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField fullWidth size="small" type="number" label="Precio envío" value={form.precio_envio} onChange={(e) => set('precio_envio', e.target.value)} inputProps={{ min: 0 }} />
            </Grid>
            {facturacionHabilitada && (
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth size="small" type="number" label="Punto de Venta AFIP"
                  value={form.punto_venta}
                  onChange={(e) => set('punto_venta', e.target.value)}
                  inputProps={{ min: 1 }}
                  helperText="Vacío = usa el Punto de Venta general de Configuración > Facturación"
                />
              </Grid>
            )}
          </Grid>
        </Paper>

        {/* Usuarios asignados */}
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <People fontSize="small" /> Usuarios de la sucursal ({asignados.length})
          </Typography>
          <Divider sx={{ mb: 1 }} />
          {usuarios.length === 0 ? (
            <Typography variant="caption" color="text.secondary">No hay usuarios activos.</Typography>
          ) : (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' } }}>
              {usuarios.map((u) => (
                <FormControlLabel
                  key={u.id}
                  control={<Checkbox size="small" checked={asignados.includes(Number(u.id))} onChange={() => toggleUsuario(Number(u.id))} />}
                  label={<Typography variant="body2">{u.username}{u.perfil_nombre ? <Typography component="span" variant="caption" color="text.secondary"> · {u.perfil_nombre}</Typography> : null}</Typography>}
                />
              ))}
            </Box>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
            <Button variant="contained" color="primary" size="large" onClick={guardar} disabled={submitting}
              startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <Save />}>
              {submitting ? 'Guardando…' : (modo === 'editar' ? 'Guardar cambios' : 'Crear sucursal')}
            </Button>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
}

export default Sucursal;
